/**
 * ArrivalSequence - the reveal choreography after the boot screen:
 * apply the first-run choice's scheme/mode side effects (the HUD and scene
 * exist now), play the arrival cinematic (first run) or a quick fade
 * (returning users), greet with a welcome toast, then start the coach marks.
 *
 * The `arrivalSequenceActive()` flag covers the whole choreography, so the
 * missions intro and the Esc settings menu don't fire mid-reveal.
 */
import { applyOnboardingChoice, beginCoachMarksFor } from './onboardingFlow.js';
import { playArrivalCinematic, quickReveal } from './arrivalCinematic.js';
import { getChaseCameraRig } from '../../entities/ChaseCameraRig.js';
import { applyCardStyle, createIcon, getTopStack } from '../designSystem.js';
import { CONFIG } from '../../config/client.config.js';
import { Sparkles } from 'lucide';

/**
 * Welcome banner in the shared top-center stack (NOT a bottom toast - the
 * coach marks own bottom-center and the two would overlap).
 */
function showWelcomeBanner() {
  const banner = document.createElement('div');
  applyCardStyle(banner, { level: 'info' });
  banner.style.display = 'inline-flex';
  banner.style.alignItems = 'center';
  banner.style.gap = '8px';
  banner.style.transition = 'opacity 0.3s ease';
  banner.append(
    createIcon(Sparkles, { size: 18 }),
    document.createTextNode(`Welcome to ${CONFIG.ARRIVAL?.meetingTitle || 'Oggi'}`),
  );
  getTopStack().appendChild(banner);
  setTimeout(() => {
    banner.style.opacity = '0';
    setTimeout(() => banner.remove(), 350);
  }, 4000);
}

let active = false;

/** @param {boolean} on */
export function markArrivalActive(on) {
  active = on;
}

/** @returns {boolean} */
export function arrivalSequenceActive() {
  return active;
}

/**
 * Run the reveal. The boot screen is gone and the canvas is visible when
 * this is called.
 * @param {{levelManager?: {getCurrentScene: () => any, getCurrentCamera: () => any}}} app
 * @param {{choice: string|null, firstRun: boolean}} opts - choice is null for
 *   returning users (their stored scheme already applies).
 */
export async function runArrivalSequence(app, { choice, firstRun }) {
  try {
    if (choice) applyOnboardingChoice(choice);

    const scene = app?.levelManager?.getCurrentScene?.();
    const camera = app?.levelManager?.getCurrentCamera?.();
    if (firstRun) {
      // The cinematic lerps the player camera directly - keep the chase rig
      // out of the way for its duration (first-run users default to 'first',
      // so this is belt-and-braces).
      const chaseRig = getChaseCameraRig(scene);
      chaseRig?.suspend('arrival');
      try {
        await playArrivalCinematic(scene, camera);
      } finally {
        chaseRig?.resume('arrival');
      }
    } else {
      await quickReveal();
    }

    try {
      showWelcomeBanner();
    } catch {
      /* cosmetic */
    }

    if (firstRun && choice) beginCoachMarksFor(choice);
  } finally {
    markArrivalActive(false);
  }
}
